import * as React from "react";
import { Box, Button, Card, Divider, Link, Stack, Tab, Tabs } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
	useCampaign,
	useCampaignFields,
	useCampaignSummarySchema,
	useCreateCampaign,
	useUpdateCampaign,
} from "@/hooks/api/use-campaigns";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { RouterLink } from "@/components/core/link";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

import { AItools } from "./ai-tools";
import { BasicInformation } from "./basic-information";
import { CustomFields } from "./customFields";
import { Dispositions } from "./dispositions";
import { Scheduling } from "./scheduling";
import { Settings } from "./settings";

const defaultScheduling = [
	{
		day: 1,
		startTime: "09:00",
		endTime: "18:00",
	},
	{
		day: 2,
		startTime: "09:00",
		endTime: "18:00",
	},

	{
		day: 3,
		startTime: "09:00",
		endTime: "18:00",
	},

	{
		day: 4,
		startTime: "09:00",
		endTime: "18:00",
	},

	{
		day: 5,
		startTime: "09:00",
		endTime: "18:00",
	},
];

enum CampaignTab {
	BASIC_INFORMATION = "Campaign Settings",
	SCHEDULING = "Scheduling",
	SETTINGS = "Summary Settings",
	DISPOSITIONS = "Dispositions",
	CUSTOM_FIELDS = "Custom Fields",
	AI_TOOLS = "AI Tools",
}

const defaultTabs = [
	{ label: "Campaign Settings", value: CampaignTab.BASIC_INFORMATION },
	{ label: "Scheduling", value: CampaignTab.SCHEDULING },
];

const editTabs = [
	...defaultTabs,
	{ label: "Dispositions", value: CampaignTab.DISPOSITIONS },
	{ label: "Custom Fields", value: CampaignTab.CUSTOM_FIELDS },
	{ label: "AI Tools", value: CampaignTab.AI_TOOLS },
];

export function CreateCampaign(): React.JSX.Element {
	const { campaignId } = useParams();
	const navigate = useNavigate();

	const isEdit = !!campaignId;

	const [tabs, setTabs] = React.useState<{ label: string; value: CampaignTab }[]>(isEdit ? editTabs : defaultTabs);
	const [activeTab, setActiveTab] = React.useState(CampaignTab.BASIC_INFORMATION);

	const createCampaignMutation = useCreateCampaign();
	const updateCampaignMutation = useUpdateCampaign();

	const { data: campaign, isLoading: isCampaignLoading } = useCampaign(campaignId || "");
	const { data: fields, isLoading: isFieldsLoading } = useCampaignFields(campaignId || "");
	const { data: summarySchema, isLoading: isSummarySchemaLoading } = useCampaignSummarySchema(campaignId || "");

	React.useEffect(() => {
		if (fields?.length > 0) {
			setTabs([
				{ label: "Campaign Settings", value: CampaignTab.BASIC_INFORMATION },
				{ label: "Scheduling", value: CampaignTab.SCHEDULING },
				...(campaignId
					? [
							{ label: "Dispositions", value: CampaignTab.DISPOSITIONS },
							{ label: "Custom Fields", value: CampaignTab.CUSTOM_FIELDS },
							{ label: "AI Tools", value: CampaignTab.AI_TOOLS },
						]
					: []),
				{ label: "Summary Settings", value: CampaignTab.SETTINGS },
			]);
		}
	}, [fields, campaignId]);

	const isLoading = isCampaignLoading || isFieldsLoading || isSummarySchemaLoading;

	const methods = useForm<any>({
		defaultValues: {
			title: "",
			enabled: false,
			groupId: "",
			groupTitle: "",
			agentId: "",
			agentTitle: "",
			phoneNumbers: [],
			settings: {},
			scheduling: isEdit ? [] : defaultScheduling,
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			startAt: "",
			endAt: "",
			callBacks: [],
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setValue,
		trigger,
		setError,
		clearErrors,
		watch,
		formState: { isSubmitting, errors },
	} = methods;

	const groupTitle = watch("groupTitle");
	const groupId = watch("groupId");
	const agentId = watch("agentId");
	const agentTitle = watch("agentTitle");
	const phoneNumbers = watch("phoneNumbers");
	const scheduling = watch("scheduling");
	const callBacks = watch("callBacks");

	React.useEffect(() => {
		if (campaign) {
			reset({
				title: campaign.title,
				enabled: campaign.enabled,
				groupId: campaign.group?.id,
				groupTitle: campaign.group?.name,
				agentId: campaign.agent?.id,
				agentTitle: campaign.agent?.name,
				phoneNumbers: campaign.phoneNumbers?.map((phone: any) => ({
					label: phone.number,
					value: phone.id,
					indicator: phone.virtual,
				})),
				settings: campaign.settings,
				timezone: campaign.timezone,
				scheduling: campaign.scheduling?.map((slot) => ({
					day: slot.dow,
					startTime: slot.from,
					endTime: slot.to,
				})),
				startAt: campaign.startAt,
				endAt: campaign.endAt,
				callBacks: campaign.callBacks,
			});
		}
	}, [campaign, reset]);

	const onSubmitForm = async (data: any) => {
		if (!data.title) {
			if (!data.title) setError("title", { message: "Title is required" });

			setActiveTab(CampaignTab.BASIC_INFORMATION);
			return;
		} else if (!data.timezone) {
			setError("timezone", { message: "Timezone is required" });
			setActiveTab(CampaignTab.SCHEDULING);
			return;
		} else if (fields?.some((field: any) => field.required && !data.settings?.[field.name] && field?.name in data.settings)) {
			for (const field of fields) {
				if (field.required && !data.settings?.[field.name]) {
					setError(`settings.${field.name}`, { message: `${field.label} is required` });
				}
			}

      if (fields?.some((field: any) => field.tab === 'main' && field.required && !data.settings?.[field.name])) {
        setActiveTab(CampaignTab.BASIC_INFORMATION);
      } else {
        setActiveTab(CampaignTab.SETTINGS);
      }

      return;
		} else if (data.callBacks?.some((cb: any) => !cb.disposition || !cb.time || !cb.tries)) {
			for (let i = 0; i < data.callBacks.length; i++) {
				const cb = data.callBacks[i];
				if (!cb.disposition) setError(`callBacks.${i}.disposition`, { message: "Disposition is required" });
				if (!cb.time) setError(`callBacks.${i}.time`, { message: "Time is required" });
				if (!cb.tries) setError(`callBacks.${i}.tries`, { message: "Tries is required" });
			}
			setActiveTab(CampaignTab.SCHEDULING);
			return;
		}

		try {
			const campaignData = {
				title: data.title,
				enabled: data.enabled,
				groupId: data.groupId || null,
				agentId: data.agentId || null,
				phoneNumbers: data.phoneNumbers?.map((phoneNumber: any) => phoneNumber.value),
				settings: data.settings,
				timezone: data.timezone,
				scheduling: data?.scheduling?.map((slot) => ({
					dow: slot.day,
					from: slot.startTime,
					to: slot.endTime,
				})),
				startAt: data.startAt,
				endAt: data.endAt,
				callBacks: data.callBacks,
			};

			const response = await (isEdit && campaign?.id
				? updateCampaignMutation.mutateAsync({ id: campaign.id, data: campaignData })
				: createCampaignMutation.mutateAsync(campaignData));

			if (isEdit) {
				navigate("/campaigns");
			} else {
				navigate(`/campaigns/${response.id}`, { replace: true });
			}
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				toast.error(errorMessages);
				return;
			}
			const errorMessage = errorMessages[0];

			const key = errorMessage.split(" ")[0];
			const tabIndex = Number(key.split(".")[1]);

      if (['startAt', 'endAt', 'scheduling', 'timezone', 'callBacks'].includes(key)) {
        setActiveTab(CampaignTab.SCHEDULING);
        setError(key, { message: errorMessage });
      } else {
        setActiveTab(tabs[tabIndex + 1]?.value as CampaignTab || CampaignTab.BASIC_INFORMATION);
        setTimeout(() => {
          trigger();
        }, 200);
      }
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: CampaignTab) => {
		setActiveTab(newValue);
		clearErrors();
	};

	const isNotFormTab =
		activeTab === CampaignTab.DISPOSITIONS ||
		activeTab === CampaignTab.CUSTOM_FIELDS ||
		activeTab === CampaignTab.AI_TOOLS;

  const mainFields = fields?.filter((field: any) => field.tab === 'main');
  const summaryFields = fields?.filter((field: any) => field.tab === 'summary');

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/campaigns"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Campaigns
							</Link>
							{isEdit && (
								<>
									<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
										/
									</Typography>
									{isCampaignLoading ? (
										<CustomSkeleton width={100} height={20} />
									) : (
										<Link
											color="text.primary"
											component={RouterLink}
											href={`/campaigns/${campaignId}/view`}
											sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
											variant="subtitle2"
										>
											{campaign?.title}
										</Link>
									)}
								</>
							)}
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								{isEdit ? "Edit" : "Create"}
							</Typography>
						</Breadcrumbs>

						<Typography variant="h4">{isEdit ? "Campaign Edit" : "Campaign Create"}</Typography>
					</Stack>

					<Card sx={{ position: "relative" }}>
						<ComponentLoader loading={isLoading} />
						<Tabs onChange={handleTabChange} sx={{ px: 3 }} value={activeTab} variant="scrollable">
							{tabs.map((tab) => (
								<Tab
									iconPosition="end"
									key={tab.value}
									label={tab.label}
									sx={{ minHeight: "auto" }}
									tabIndex={0}
									value={tab.value}
								/>
							))}
						</Tabs>

						<Divider />

						<Box sx={isNotFormTab ? { p: 0 } : { p: 2, py: 2.5 }}>
							<FormProvider {...methods}>
								{activeTab === CampaignTab.BASIC_INFORMATION && (
									<BasicInformation
										control={control}
										errors={errors}
										setValue={setValue}
										groupTitle={groupTitle}
										groupId={groupId}
										agentTitle={agentTitle}
										agentId={agentId}
										phoneNumbers={phoneNumbers}
										campaign={campaign}
                    mainFields={mainFields}
									/>
								)}

								{activeTab === CampaignTab.SETTINGS && (
									<Settings control={control} fields={summaryFields} summarySchema={summarySchema} />
								)}

								{activeTab === CampaignTab.SCHEDULING && (
									<Scheduling
										isEdit={isEdit}
										control={control}
										errors={errors}
										setValue={setValue}
										scheduling={scheduling}
										callBacks={callBacks}
										clearErrors={clearErrors}
									/>
								)}

								{activeTab === CampaignTab.DISPOSITIONS && <Dispositions />}
								{activeTab === CampaignTab.CUSTOM_FIELDS && <CustomFields />}
								{activeTab === CampaignTab.AI_TOOLS && <AItools />}

								<Button
									type="submit"
									onClick={handleSubmit(onSubmitForm)}
									variant="contained"
									disabled={isSubmitting || createCampaignMutation.isPending || updateCampaignMutation.isPending}
									sx={isNotFormTab ? { my: 2.5, mx: 2 } : { mt: 2.5 }}
								>
									{isEdit ? "Update Campaign" : "Create Campaign"}
								</Button>
							</FormProvider>
						</Box>
					</Card>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
